import { useState, useRef, useEffect } from 'react';
import { Mic, Camera, Send, Loader2, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import type { ChatMessage, ChatbotResponse, Tool } from '@tools-beaver/shared';
import { apiClient } from '../../api/client';
import { ToolCard } from '../tools/ToolCard';

interface AskTimProps {
  onResults?: (tools: Tool[]) => void;
}

interface ChatEntry extends ChatMessage {
  tools?: Tool[];
  action?: string;
}

export function AskTim({ onResults }: AskTimProps) {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [history, setHistory] = useState<ChatEntry[]>([]);
  const [showChat, setShowChat] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [history, isLoading]);

  const handleSubmit = async (e: React.FormEvent | React.KeyboardEvent | string) => {
    if (typeof e !== 'string') e.preventDefault();
    const message = typeof e === 'string' ? e : input.trim();
    if (!message || isLoading) return;

    setInput('');
    setShowChat(true);

    const userEntry: ChatEntry = { role: 'user', content: message };
    setHistory((prev) => [...prev, userEntry]);
    setIsLoading(true);

    try {
      const { data } = await apiClient.post<{ success: boolean; data: ChatbotResponse }>(
        '/chatbot/chat',
        {
          message,
          history: history.map(({ role, content }) => ({ role, content })),
        }
      );

      const resp = data.data;
      const assistantEntry: ChatEntry = {
        role: 'assistant',
        content: resp.message,
        tools: resp.toolSuggestions,
        action: resp.action,
      };

      setHistory((prev) => [...prev, assistantEntry]);

      if (resp.toolSuggestions && onResults) {
        onResults(resp.toolSuggestions);
      }
    } catch {
      setHistory((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: "Hmm, I'm having trouble connecting right now. Try searching for the tool directly!",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const suggestions = [
    'I need to hang some shelves',
    'Looking for a power drill',
    'Mowing my lawn this weekend',
    'Building a deck',
  ];

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Chat thread */}
      {showChat && history.length > 0 && (
        <div className="mb-4 bg-white rounded-3xl shadow-card overflow-hidden animate-slide-up">
          <div className="flex items-center justify-between px-4 py-3 border-b border-silver-100">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-garden-500 flex items-center justify-center text-white text-sm font-bold">
                T
              </div>
              <span className="font-semibold text-silver-800 text-sm">Tim the Beaver</span>
              <span className="w-2 h-2 rounded-full bg-garden-400 animate-pulse-green" />
            </div>
            <button
              onClick={() => { setShowChat(false); setHistory([]); }}
              className="text-silver-400 hover:text-silver-600 transition-colors"
              aria-label="Close chat"
            >
              <X size={16} />
            </button>
          </div>

          <div ref={chatScrollRef} className="max-h-80 overflow-y-auto p-4 space-y-3">
            {history.map((entry, i) => (
              <div
                key={i}
                className={`flex ${entry.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    entry.role === 'user'
                      ? 'bg-garden-500 text-white rounded-br-sm'
                      : 'bg-silver-50 text-silver-800 rounded-bl-sm'
                  }`}
                >
                  {entry.role === 'assistant' ? (
                    <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-1 prose-strong:text-silver-900">
                      <ReactMarkdown>{entry.content}</ReactMarkdown>
                    </div>
                  ) : (
                    entry.content
                  )}
                  {entry.tools && entry.tools.length > 0 && (
                    <div className="mt-3 grid grid-cols-1 gap-2">
                      {entry.tools.slice(0, 3).map((tool) => (
                        <ToolCard key={tool.id} tool={tool} compact />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-silver-50 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1.5">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="w-2 h-2 rounded-full bg-silver-400 animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search input */}
      <div className="ask-tim-input relative bg-white rounded-3xl border border-silver-200 shadow-card transition-all duration-200">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
          placeholder="Ask Tim…"
          className="w-full bg-transparent px-5 py-4 pr-28 text-silver-900 placeholder-silver-400
                     text-base focus:outline-none rounded-3xl"
          aria-label="Ask Tim about tools you need"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          <button
            type="button"
            className="p-2.5 rounded-2xl text-silver-400 hover:text-garden-600 hover:bg-garden-50
                       transition-colors"
            aria-label="Voice search"
          >
            <Mic size={18} />
          </button>
          <button
            type="button"
            className="p-2.5 rounded-2xl text-silver-400 hover:text-garden-600 hover:bg-garden-50
                       transition-colors"
            aria-label="Image search"
          >
            <Camera size={18} />
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e)}
            disabled={!input.trim() || isLoading}
            className="p-2.5 rounded-2xl bg-garden-500 text-white hover:bg-garden-600
                       disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            aria-label="Search"
          >
            {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>

      {/* Quick suggestion pills */}
      {!showChat && (
        <div className="mt-3 flex flex-wrap justify-center gap-2 animate-fade-in">
          {suggestions.map((s) => (
            <button
              key={s}
              onClick={() => { setInput(s); handleSubmit(s); }}
              className="text-xs text-silver-600 bg-white border border-silver-200 rounded-full
                         px-3 py-1.5 hover:border-garden-400 hover:text-garden-600 hover:bg-garden-50
                         transition-all duration-150"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
